import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { getCurrentUser } from '../lib/auth';
import toast from 'react-hot-toast';

interface WithdrawalOption {
  amount: number;
  fee: number;
  netAmount: number;
}

interface PaymentInfo {
  method: 'paypal' | 'bank' | 'western_union';
  details: {
    [key: string]: string;
  };
}

export default function Withdraw() {
  const [loading, setLoading] = useState(false);
  const [balance, setBalance] = useState(0);
  const [selectedOption, setSelectedOption] = useState<WithdrawalOption | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<PaymentInfo['method'] | ''>('');
  const [paymentDetails, setPaymentDetails] = useState<{ [key: string]: string }>({});
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [lastWithdrawal, setLastWithdrawal] = useState<string | null>(null);
  const user = getCurrentUser();

  const withdrawalOptions: WithdrawalOption[] = [
    { amount: 100, fee: 6, netAmount: 94 },
    { amount: 500, fee: 20, netAmount: 480 },
    { amount: 1000, fee: 50, netAmount: 950 }
  ];

  useEffect(() => {
    loadUserData();
    checkLastWithdrawal();
  }, []);

  const loadUserData = async () => {
    try {
      const userJson = localStorage.getItem('user');
      if (!userJson) return;
      const user = JSON.parse(userJson);
      setBalance(user.wallet_balance);
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  };

  const checkLastWithdrawal = async () => {
    if (!user) return;

    try {
      const today = new Date().toISOString().split('T')[0];
      const { data, error } = await supabase
        .from('stripetransactions')
        .select('created_at')
        .eq('user_id', user.id)
        .eq('type', 'withdraw')
        .gte('created_at', today)
        .order('created_at', { ascending: false })
        .limit(1);

      if (error) throw error;
      if (data && data.length > 0) {
        setLastWithdrawal(data[0].created_at);
      }
    } catch (error) {
      console.error('Error checking last withdrawal:', error);
    }
  };

  const validatePaymentDetails = () => {
    if (!paymentMethod) return false;

    switch (paymentMethod) {
      case 'paypal':
        return paymentDetails.email && paymentDetails.email.includes('@');
      case 'bank':
        return (
          paymentDetails.accountNumber?.length >= 8 &&
          paymentDetails.routingNumber?.length >= 9 &&
          paymentDetails.swiftCode?.length >= 8
        );
      case 'western_union':
        return (
          paymentDetails.fullName?.length >= 3 &&
          paymentDetails.address &&
          paymentDetails.city &&
          paymentDetails.country
        );
      default:
        return false;
    }
  };

  const handleWithdraw = async () => {
    if (!user || !selectedOption || !validatePaymentDetails()) return;

    // Check for daily withdrawal limit
    if (lastWithdrawal) {
      toast.error('You can only make one withdrawal per day');
      return;
    }

    // Check sufficient balance
    if (balance < selectedOption.amount) {
      toast.error('Insufficient balance');
      return;
    }

    setLoading(true);
    try {
      // Create withdrawal transaction
      const { error: transactionError } = await supabase
        .from('stripetransactions')
        .insert({
          user_id: user.id,
          amount: selectedOption.amount,
          type: 'withdraw',
          status: 'completed'
        });

      if (transactionError) throw transactionError;

      // Update user's balance
      const newBalance = balance - selectedOption.amount;
      const { error: updateError } = await supabase
        .from('stripeusers')
        .update({ wallet_balance: newBalance })
        .eq('id', user.id);

      if (updateError) throw updateError;

      // Update local storage
      const userJson = localStorage.getItem('user');
      if (userJson) {
        const userData = JSON.parse(userJson);
        userData.wallet_balance = newBalance;
        localStorage.setItem('user', JSON.stringify(userData));
      }

      toast.success('Withdrawal request submitted successfully');
      setBalance(newBalance);
      setSelectedOption(null);
      setPaymentMethod('');
      setPaymentDetails({});
      setShowConfirmation(false);
      setLastWithdrawal(new Date().toISOString());
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const renderPaymentFields = () => {
    switch (paymentMethod) {
      case 'paypal':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                PayPal Email
              </label>
              <input
                type="email"
                value={paymentDetails.email || ''}
                onChange={(e) => setPaymentDetails({ ...paymentDetails, email: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="your.email@example.com"
                required
              />
            </div>
          </div>
        );

      case 'bank':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Account Number
              </label>
              <input
                type="text"
                value={paymentDetails.accountNumber || ''}
                onChange={(e) => setPaymentDetails({ ...paymentDetails, accountNumber: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Enter account number"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Routing Number
              </label>
              <input
                type="text"
                value={paymentDetails.routingNumber || ''}
                onChange={(e) => setPaymentDetails({ ...paymentDetails, routingNumber: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Enter routing number"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                SWIFT/BIC Code
              </label>
              <input
                type="text"
                value={paymentDetails.swiftCode || ''}
                onChange={(e) => setPaymentDetails({ ...paymentDetails, swiftCode: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Enter SWIFT/BIC code"
                required
              />
            </div>
          </div>
        );

      case 'western_union':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Full Name
              </label>
              <input
                type="text"
                value={paymentDetails.fullName || ''}
                onChange={(e) => setPaymentDetails({ ...paymentDetails, fullName: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Enter full name"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Address
              </label>
              <input
                type="text"
                value={paymentDetails.address || ''}
                onChange={(e) => setPaymentDetails({ ...paymentDetails, address: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Enter address"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  City
                </label>
                <input
                  type="text"
                  value={paymentDetails.city || ''}
                  onChange={(e) => setPaymentDetails({ ...paymentDetails, city: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Enter city"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Country
                </label>
                <input
                  type="text"
                  value={paymentDetails.country || ''}
                  onChange={(e) => setPaymentDetails({ ...paymentDetails, country: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Enter country"
                  required
                />
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Withdraw Funds</h1>
            <p className="mt-1 text-sm text-gray-500">
              Choose your withdrawal amount and payment method
            </p>
          </div>
          <Link
            to="/wallet"
            className="inline-flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-500"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Wallet
          </Link>
        </div>

        {/* Balance Card */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden mb-8">
          <div className="p-6 bg-gradient-to-r from-indigo-500 to-purple-600 text-white">
            <p className="text-sm font-medium opacity-80">Available Balance</p>
            <h2 className="mt-2 text-3xl font-bold">${balance.toFixed(2)}</h2>
          </div>
        </div>

        {/* Daily Limit Warning */}
        {lastWithdrawal && (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-8 rounded-lg">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-yellow-700">
                  You've already made a withdrawal today. Please try again tomorrow.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Withdrawal Options */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden mb-8">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Select Amount</h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {withdrawalOptions.map((option) => (
                <button
                  key={option.amount}
                  onClick={() => setSelectedOption(option)}
                  className={`p-4 rounded-xl border-2 transition-colors duration-200 ${
                    selectedOption?.amount === option.amount
                      ? 'border-indigo-600 bg-indigo-50'
                      : 'border-gray-200 hover:border-indigo-200'
                  }`}
                >
                  <div className="text-center">
                    <p className="text-2xl font-bold text-gray-900">${option.amount}</p>
                    <p className="text-sm text-gray-500 mt-1">Fee: ${option.fee}</p>
                    <p className="text-base font-medium text-indigo-600 mt-2">
                      Net: ${option.netAmount}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Payment Method */}
        {selectedOption && (
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden mb-8">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Payment Method</h3>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <button
                  onClick={() => setPaymentMethod('paypal')}
                  className={`p-4 rounded-xl border-2 transition-colors duration-200 ${
                    paymentMethod === 'paypal'
                      ? 'border-indigo-600 bg-indigo-50'
                      : 'border-gray-200 hover:border-indigo-200'
                  }`}
                >
                  <div className="flex items-center justify-center">
                    <svg className="w-6 h-6 mr-2 text-[#00457C]" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.987 1.62 1.407 1.074 2.122 2.609 2.122 4.581 0 .901-.144 1.738-.43 2.513-.286.776-.696 1.448-1.228 2.013-.532.565-1.195 1.036-1.987 1.41-.793.373-1.697.634-2.72.776-.24.033-.49.056-.75.072-.29.016-.57.024-.84.024h-.48c-.306 0-.604.079-.87.23-.265.15-.473.37-.621.656a2.136 2.136 0 0 0-.242.964l-.049.282-.381 2.377c-.067.412-.363.69-.747.69H7.076v-.001zm8.425-15.75c-.685 0-1.277.04-1.782.119-.506.08-.99.222-1.46.428-.47.207-.876.51-1.22.913-.346.404-.6.934-.763 1.59l-.157.79c-.067.34.075.677.364.87.29.193.65.289 1.082.289h.782c.913 0 1.71-.039 2.395-.116.684-.077 1.333-.243 1.955-.496.62-.253 1.17-.62 1.652-1.1.48-.478.84-1.108 1.076-1.887.236-.779.354-1.747.354-2.901 0-.858-.146-1.554-.428-2.084-.283-.53-.688-.93-1.22-1.195-.53-.265-1.156-.44-1.88-.523-.724-.084-1.52-.126-2.395-.126h-.355l.002-.002z"/>
                    </svg>
                    <span className="font-medium">PayPal</span>
                  </div>
                </button>
                <button
                  onClick={() => setPaymentMethod('bank')}
                  className={`p-4 rounded-xl border-2 transition-colors duration-200 ${
                    paymentMethod === 'bank'
                      ? 'border-indigo-600 bg-indigo-50'
                      : 'border-gray-200 hover:border-indigo-200'
                  }`}
                >
                  <div className="flex items-center justify-center">
                    <svg className="w-6 h-6 mr-2 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l9-4 9 4v2H3V6zm0 12h18V8H3v10zm4-7h2v4H7v-4zm4 0h2v4h-2v-4z" />
                    </svg>
                    <span className="font-medium">Bank Transfer</span>
                  </div>
                </button>
                <button
                  onClick={() => setPaymentMethod('western_union')}
                  className={`p-4 rounded-xl border-2 transition-colors duration-200 ${
                    paymentMethod === 'western_union'
                      ? 'border-indigo-600 bg-indigo-50'
                      : 'border-gray-200 hover:border-indigo-200'
                  }`}
                >
                  <div className="flex items-center justify-center">
                    <svg className="w-6 h-6 mr-2 text-[#FFDD00]" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm0 22C6.486 22 2 17.514 2 12S6.486 2 12 2s10 4.486 10 10-4.486 10-10 10zm0-18c-4.411 0-8 3.589-8 8s3.589 8 8 8 8-3.589 8-8-3.589-8-8-8zm0 14c-3.309 0-6-2.691-6-6s2.691-6 6-6 6 2.691 6 6-2.691 6-6 6z"/>
                    </svg>
                    <span className="font-medium">Western Union</span>
                  </div>
                </button>
              </div>

              {/* Payment Details Form */}
              {paymentMethod && renderPaymentFields()}
            </div>
          </div>
        )}

        {/* Submit Button */}
        {selectedOption && paymentMethod && (
          <button
            onClick={() => setShowConfirmation(true)}
            disabled={!validatePaymentDetails() || loading || !!lastWithdrawal}
            className="w-full bg-indigo-600 text-white py-3 px-4 rounded-xl font-medium hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
          >
            {loading ? 'Processing...' : 'Withdraw Funds'}
          </button>
        )}

        {/* Confirmation Modal */}
        {showConfirmation && selectedOption && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl shadow-xl max-w-md w-full">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-xl font-semibold text-gray-900">Confirm Withdrawal</h3>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Amount:</span>
                    <span className="font-medium">${selectedOption.amount}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Fee:</span>
                    <span className="font-medium">${selectedOption.fee}</span>
                  </div>
                  <div className="flex justify-between text-lg font-semibold">
                    <span>Net Amount:</span>
                    <span>${selectedOption.netAmount}</span>
                  </div>
                </div>
                <div className="mt-6 flex space-x-4">
                  <button
                    onClick={() => setShowConfirmation(false)}
                    className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleWithdraw}
                    disabled={loading}
                    className="flex-1 px-4 py-3 bg-indigo-600 text-white rounded-xl font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                  >
                    {loading ? 'Processing...' : 'Confirm'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}